import "../App.css"
import React, { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next'
import { useNavigate, Link } from 'react-router-dom';
import GlobalHeaader from '../components/GlobalHeader';
import GlobalFooter from '../components/GlobalFooter';
import BreadcrumbTrail from '../components/BreadcurmbTrail';
import Menu from '../components/Menu';
import ReportProblem from '../components/ReportProblem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import { getElements } from "../api/blawksAPI";
import { getTranslatedText } from "./utils_v2/kumuHelper";
import Provinces from "./Provinces";

export default function Home() {

    const { t, i18n } = useTranslation('translation');
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [elements, setElements] = useState([]);
    const kumuElements = require('../api/kumu_elements.json');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0)
        setElements(kumuElements.rows.filter((elm) => elm.value.attributes['element type'] === 'result'))
    }, [])

    useEffect(() => {
        const timer = setTimeout(() => {
            const ua = navigator.userAgent;
            const isMobile =
                /Mobi|Android|iPhone|iPod/i.test(ua) ||
                (navigator.maxTouchPoints > 1 && window.innerWidth <= 1024);
            if (isMobile) {
                alert(t("For the best experience, please use a desktop or laptop."));
            }
        }, 3000);
        return () => clearTimeout(timer);
    }, []);

    function stripHtml(htmlString) {
        const doc = new DOMParser().parseFromString(htmlString, 'text/html');
        return doc.body.textContent || "";
    }

    const sortedElements = [...elements].sort((a, b) => {
        const labelA = stripHtml(getTranslatedText(i18n.language, a.value.attributes.label)).toLowerCase();
        const labelB = stripHtml(getTranslatedText(i18n.language, b.value.attributes.label)).toLowerCase();
        return labelA.localeCompare(labelB);
    });

    return (
        <div>
            <header>
                <GlobalHeaader />
                <Menu />
                <BreadcrumbTrail />
            </header>

            <main property="mainContentOfPage" className="page-content container" typeof="WebPageElement">

                {/* ── Intro section ── */}
                <section data-ga="{&quot;category&quot;:&quot;Most requested&quot;}" style={{ padding: '0 10px' }}>
                    <div className="row profile">
                        <div className="row-md-7">
                            <h1 property="name" id="wb-cont">{t("Mine Permit Navigator")}</h1>
                            <p><Trans i18nKey="minePermitNavigatorText" components={{ bold: <b /> }} /></p>
                        </div>

                        <div className="row-md-5" style={{ marginBottom: '20px', marginTop: '20px' }}>
                            <h2 style={{ fontSize: "28px" }}>{t("Using the Mine Permit Navigator")}</h2>
                        </div>

                        {/* Two icon+text columns — stack on mobile */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '15px',
                                    flex: '1 1 280px', // grows, but wraps below ~280px
                                }}
                            >
                                <img
                                    src="/static/graphics/mine_icon.png"
                                    alt="Icon"
                                    className="hidden-sm hidden-xs"
                                    style={{ width: '80px', height: '80px', objectFit: 'cover', flexShrink: 0 }}
                                />
                                <div>
                                    <p>{t("To begin your search for legislation relevant to your mining project, you will:")}</p>
                                    <ul>
                                        <li>{t("identify planned mining activities")}</li>
                                        <li>{t("answer questions about project details")}</li>
                                    </ul>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '15px',
                                    flex: '1 1 280px',
                                }}
                            >
                                <img
                                    src="/static/graphics/scale_icon.png"
                                    alt="Icon"
                                    className="hidden-sm hidden-xs"
                                    style={{ width: '80px', height: '80px', objectFit: 'cover', flexShrink: 0 }}
                                />
                                <div>
                                    <p>{t("Based on the information provided, the Mine Permit Navigator will:")}</p>
                                    <ul>
                                        <li>{t("list federal legislation that may apply to your project")}</li>
                                        <li>{t("outline responsibilities under the legislation")}</li>
                                        <li>{t("provide additional resources to guide you through the application process")}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: '30px' }}>
                            <Link
                                className="btn btn-call-to-action"
                                style={{ width: "100%", height: '67px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                to={`/${i18n.language}/search`}
                            >
                                <h3 style={{ margin: 0 }}>{t("Begin your search")}</h3>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* ── Search + FAQ cards ── */}
                <section data-ga="{&quot;category&quot;:&quot;Most requested&quot;}" style={{ marginTop: '25px', padding: '0 10px' }}>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '20px',
                        alignItems: 'stretch',
                    }}>
                        {/* Search card */}
                        <div style={{
                            background: '#D9EDF7',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '20px',
                            flex: '2 1 300px', // flex-grow:2, shrinks, min ~300px before wrapping
                            borderRadius: '4px',
                        }}>
                            <h2 style={{ fontSize: "24px", textAlign: "center", marginBottom: "16px" }}>
                                {t("Already know what permit you are looking for?")}
                            </h2>
                            <div style={{ marginBottom: "24px" }}>
                                <Trans
                                    i18nKey="Search all the federal legislation included in the Mine Permit Navigator here and get guidance on filling out applications, who to contact and how long it might take."
                                    components={{ bold: <b /> }}
                                />
                            </div>
                            <section>
                                {isLoading ? <Skeleton variant="text" height={75} /> : (
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} sm={10}>
                                            <Autocomplete
                                                fullWidth
                                                disablePortal
                                                popupIcon={""}
                                                options={sortedElements}
                                                onChange={(e, v) => { setSearch(v?.value?.attributes?.['reference id'] || null) }}
                                                sx={{
                                                    borderRadius: '10px',
                                                    backgroundColor: 'white',
                                                    '& .MuiAutocomplete-input, & .MuiInputLabel-root': { fontSize: 16 },
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="" placeholder={t("Type name of permit")} />
                                                )}
                                                getOptionLabel={(option) => stripHtml(getTranslatedText(i18n.language, option.value.attributes.label))}
                                                renderOption={(props, option) => (
                                                    <Box style={{ fontSize: 16 }} {...props}>
                                                        {stripHtml(getTranslatedText(i18n.language, option.value.attributes.label))}
                                                    </Box>
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <button
                                                className="btn btn-primary"
                                                style={{ height: '55px', width: '100%' }}
                                                onClick={() => { if (search) navigate(`/${i18n.language}/overview/${search}`) }}
                                            >
                                                <span className="glyphicon-search glyphicon" />
                                                {/* Always show text on xs so it's not just an icon */}
                                            </button>
                                        </Grid>
                                    </Grid>
                                )}
                            </section>
                        </div>

                        {/* Survey card */}
                        <div style={{
                            border: "1px solid #284162",
                            padding: '20px',
                            flex: '1 1 240px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px',
                            textAlign: 'center',
                        }}>
                        {/* Orange list icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="70" height="70" viewBox="0 0 24 24" style={{ marginBottom: '16px' }}>
                            <path d="M0 0h24v24H0z" fill="none" />
                            <g fill="none" stroke="#F5A623" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5">
                                <path d="M20 12V5.749a.6.6 0 0 0-.176-.425l-3.148-3.148A.6.6 0 0 0 16.252 2H4.6a.6.6 0 0 0-.6.6v18.8a.6.6 0 0 0 .6.6H11M8 10h8M8 6h4m-4 8h3m6.954 2.94l1-1a1.12 1.12 0 0 1 1.586 0v0a1.12 1.12 0 0 1 0 1.585l-1 1m-1.586-1.586l-2.991 2.991a1 1 0 0 0-.28.553l-.244 1.557l1.557-.243a1 1 0 0 0 .553-.28l2.99-2.992m-1.585-1.586l1.586 1.586" />
                                <path d="M16 2v3.4a.6.6 0 0 0 .6.6H20" />
                            </g>
                        </svg>

                            <h2 style={{ fontSize: "20px", marginBottom: "12px" }}>
                                <a    
                                    href={i18n.language === 'fr'
                                        ? 'https://forms-formulaires.alpha.canada.ca/fr/id/cmon8ej8u00uux201b33q2j0s'
                                        : 'https://forms-formulaires.alpha.canada.ca/en/id/cmon8ej8u00uux201b33q2j0s'
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#284162', fontWeight: 'bold' }}
                                >
                                    {t("Take our 5-minute survey")}
                                </a>
                            </h2>

                            <p style={{ margin: 0 }}>
                                {t("Tell us about your experience using the Mine Permit Navigator. Your input will help shape future improvements.")}
                            </p>
                        </div>
                    </div>
                </section>

                <br />

                {/* ── Useful Links ── */}
                <section style={{ marginTop: '25px', marginBottom: '25px' }}>
                    <h2 className="mrgn-tp-md" style={{ fontSize: "28px" }}>{t("Useful Links")}</h2>
                    <p>{t("These useful links may assist with your responses to questions within the tool:")}</p>

                    {/* Top row: 2 columns */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                        {/* Wildlife, species and habitat */}
                        <div style={{ flex: '1 1 300px' }}>
                            <h3 style={{ fontSize: "20px", fontWeight: "bold" }}>{t("Wildlife, species and habitat")}</h3>
                            <ul>
                                <li><a href={i18n.language === 'fr' ? 'https://www.canada.ca/fr/environnement-changement-climatique/services/protection-legale-oiseaux-migrateurs/liste.html' : 'https://www.canada.ca/en/environment-climate-change/services/migratory-birds-legal-protection/list.html'} target='_blank'>{t("Birds protected in Canada")}</a></li>
                                <li><a href={`https://species-registry.az.ec.gc.ca/index-${i18n.language}.html#/species?sortBy=commonNameSort&sortDirection=asc&pageSize=10`} target='_blank'>{t("Species at risk public registry")}</a></li>
                                <li><a href={`https://www.dfo-mpo.gc.ca/species-especes/sara-lep/map-carte/index-${i18n.language === 'fr' ? 'fra' : 'eng'}.html`} target='_blank'>{t("Aquatic species at risk map")}</a></li>
                                <li><a href={`https://osdp-psdo.canada.ca/dp/${i18n.language}/explore?search_event=EXT_CANMET`} target='_blank'>{t("Critical habitat map")}</a></li>
                            </ul>
                        </div>

                        {/* Environment, regulation and public safety */}
                        <div style={{ flex: '1 1 300px' }}>
                            <h3 style={{ fontSize: "20px", fontWeight: "bold" }}>{t("Environment, regulation and public safety")}</h3>
                            <ul>
                                <li><a href={i18n.language === 'fr' ? 'https://www.canada.ca/fr/environnement-changement-climatique/services/gestion-substances-toxiques/liste-loi-canadienne-protection-environnement.html' : 'https://www.canada.ca/en/environment-climate-change/services/management-toxic-substances/list-canadian-environmental-protection-act.html'} target='_blank'>{t("List of toxic substances managed under Canadian Environmental Protection Act")}</a></li>
                                <li><a href={`https://eservices.nrcan-rncan.gc.ca/web/elms-esml/products-produits`} target='_blank'>{t("List of Authorized Explosives")}</a></li>
                                <li><a href={i18n.language === 'fr' ? 'https://www.canada.ca/fr/agence-evaluation-impact/programmes/evaluation-impact-101.html' : 'https://www.canada.ca/en/impact-assessment-agency/programs/impact-assessments-101.html'} target='_blank'>{t("Impact assessment 101")}</a></li>
                            </ul>
                        </div>
                    </div>

                    {/* Bottom row: full width */}
                    <div>
                        <h3 style={{ fontSize: "20px", fontWeight: "bold" }}>{t("Science and data resources")}</h3>
                        <ul>
                            <li><a href={`/${i18n.language}/faq`} target='_blank'>{t("Frequently Asked Questions about key legislation")}</a></li>
                            <li><a href={`https://osdp-psdo.canada.ca/dp/${i18n.language}`} target='_blank'>{t("Open Science and Data Platform")}</a></li>
                        </ul>
                    </div>
                    <div>
                        <h3 style={{ fontSize: "20px", fontWeight: "bold" }}>{t("Regional resources")}</h3>
                        <p>
                            {t("To find mine permitting information and resources relevant to your region, please use the links listed under each province and territory on the ")}
                            <a 
                                href={`/${i18n.language}/regional-resources`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ textDecoration: 'underline' }}
                            >
                                {t("Provincial and Territorial Resources page")}
                            </a>
                            {t(".")}
                        </p>
                    </div>
                </section>

                {/* ── Contact ── */}
                <section data-ga="{&quot;category&quot;:&quot;Most requested&quot;}" style={{ padding: '0 10px' }}>
                    <div className="row profile">
                        <div className="row-md-7">
                            <h2 style={{ fontSize: "28px" }}>{t("Contact")}</h2>
                            <p>
                                <Trans
                                    i18nKey="If you have any suggestions or questions about Mine Permit Navigator, please contact us at minepermitnavigator-navigateurdepermisminiers@nrcan-rncan.gc.ca."
                                    components={{ a: <a href="mailto:minepermitnavigator-navigateurdepermisminiers@nrcan-rncan.gc.ca" /> }}
                                />
                            </p>
                            <p>
                                {t("For contact information related to specific legislation or departments, please refer to the ")}
                                <a href={`/${i18n.language}/faq`} target="_blank" rel="noopener noreferrer">
                                    {t("FAQ page")}
                                </a>
                                {t(".")}
                            </p>
                        </div>
                    </div>
                </section>

                <br />

                {/* ── Disclaimer ── */}
                <section className="most-requested well well-sm brdr-1" style={{ marginTop: '25px' }}>
                    <h2 className="mrgn-tp-md" style={{ fontSize: "28px", padding: '0 10px' }}>{t("Disclaimer")}</h2>
                    <div className="row profile">
                        <div className="col-md-12" style={{ padding: '0 25px' }}>
                            <p>
                                {t("DisclaimerPart1")}
                                <a href={`/${i18n.language}/faq`} target="_blank" rel="noopener noreferrer">{t("Frequently Asked Questions")}</a>
                                {t(".")}
                            </p>
                            <p><Trans i18nKey="DisclaimerPart2" components={{ sup: <sup /> }} /></p>
                            <p>{t("DisclaimerPart3")}</p>
                            <p>{t("DisclaimerPart4")}</p>
                        </div>
                    </div>
                </section>

            </main>

            <ReportProblem />
            <GlobalFooter />
        </div>
    );
}